import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown, ChevronRight, Check, X, Loader2, Sparkles, Landmark, FileText,
  Scale, Users, Filter, Search as SearchIcon, CircleDollarSign, AlertTriangle, HelpCircle,
  Star, FilePlus2, ExternalLink, Sprout, ListTree, Map as MapIcon, ShieldCheck, ShieldAlert, Info,
  Pencil, Wand2, FolderPlus, History, Undo2, TrendingUp,
} from "lucide-react";
import { PlannerMap } from "./PlannerMap.js";
import { ClusterEditModal } from "./ClusterEditModal.js";
import { NewClusterDialog } from "../pipeline/NewClusterDialog.js";
import type { PlannerAuditFinding } from "../../lib/api.js";
import {
  api, type PlannerClusterNode, type PlannerAssignmentView, type PlannerIntent, type PlannerPageType,
  type PlannerActionKind,
} from "../../lib/api.js";

/**
 * Cluster Planner — the taxonomy planning surface (cluster-architecture plan:
 * ONE cluster = ONE page). Left: the family → sub-family → cluster tree with
 * per-cluster rollups. Right: the assignment review queue — every research
 * keyword's proposed cluster, accepted/rejected/reassigned by the operator.
 */

const INTENT_STYLE: Record<PlannerIntent, { label: string; cls: string; title: string }> = {
  T: { label: "T", cls: "bg-emerald-100 text-emerald-800", title: "Transactional (money page)" },
  C: { label: "C", cls: "bg-teal-100 text-teal-800", title: "Commercial investigation" },
  I: { label: "I", cls: "bg-blue-100 text-blue-800", title: "Informational (editorial)" },
  B: { label: "B", cls: "bg-purple-100 text-purple-800", title: "Branded" },
};

const PAGE_TYPE_LABEL: Record<PlannerPageType, string> = {
  hub: "Hub", landing: "Landing", editorial: "Article", comparison: "Vs",
  faq_section: "FAQ §", city_method: "City×Method", pro_article: "Pro",
};

const ACTION_STYLE: Record<PlannerActionKind, { cls: string; hint: string }> = {
  review: { cls: "bg-amber-100 text-amber-800", hint: "Pending assignments to review" },
  "create-page": { cls: "bg-brand-100 text-brand-800", hint: "Ready — promote the primary keyword into the Pipeline as one article" },
  "expand-page": { cls: "bg-blue-100 text-blue-800", hint: "Owns a live page + has accepted keywords — grow the existing page (don't create a rival)" },
  live: { cls: "bg-emerald-100 text-emerald-800", hint: "Owns a live page, nothing pending" },
  fold: { cls: "bg-blue-100 text-blue-800", hint: "Section (not its own page) — fold these keywords into the parent page's brief as a section/FAQ" },
  planned: { cls: "", hint: "" },
};
const WORK_ACTIONS: PlannerActionKind[] = ["review", "create-page", "fold", "expand-page", "live"];

function IntentChip({ intent }: { intent: PlannerIntent | null }) {
  if (!intent) return null;
  const s = INTENT_STYLE[intent];
  return <span title={s.title} className={`inline-flex h-4 w-4 items-center justify-center rounded text-[10px] font-bold ${s.cls}`}>{s.label}</span>;
}

/** Cluster-size gauge vs the 3–15 rule (plan rule 2). */
function SizeGauge({ n }: { n: number }) {
  const cls = n === 0 ? "text-muted" : n < 3 ? "text-amber-600" : n <= 15 ? "text-emerald-700" : "text-danger";
  const hint = n === 0 ? "no keywords yet" : n < 3 ? "under 3 — consider merging into the parent" : n <= 15 ? "healthy size (3–15)" : "over 15 — consider splitting by modifier";
  return <span title={`${n} keyword(s) — ${hint}`} className={`text-xs font-medium tabular-nums ${cls}`}>{n}</span>;
}

/**
 * `initialView` lets the Opportunities tab mount straight into the ranked view.
 * It used to be a small third segment inside this card's header, three levels
 * deep (Keyword Research → Planner → segment) and absent from the explainer and
 * the Help guide — so in practice nobody found it.
 */
export function PlannerTab({ initialView = "tree" }: { initialView?: "tree" | "map" | "priorities" } = {}) {
  const qc = useQueryClient();
  const [openFamilies, setOpenFamilies] = useState<Set<string>>(new Set(["MTH", "COND"]));
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [queueTab, setQueueTab] = useState<"pending" | "accepted" | "rejected">("pending");
  const [clusterFilter, setClusterFilter] = useState("");
  const [actionFilter, setActionFilter] = useState<PlannerActionKind | null>(null);
  const [viewMode, setViewMode] = useState<"tree" | "map" | "priorities">(initialView);
  // Default OFF: on, this hid every cluster that isn't `create-page`, so the
  // ranked list silently omitted live pages worth strengthening — and a reader
  // had no way to tell the list was filtered.
  const [prioBuildOnly, setPrioBuildOnly] = useState(false);
  const [showAudits, setShowAudits] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [useLlm, setUseLlm] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem("planner-help-dismissed") !== "1");
  const [editCluster, setEditCluster] = useState<PlannerClusterNode | null>(null);
  const [showNewCluster, setShowNewCluster] = useState(false);
  const dismissHelp = () => { localStorage.setItem("planner-help-dismissed", "1"); setShowHelp(false); };

  const tree = useQuery({ queryKey: ["planner", "tree"], queryFn: () => api.plannerTree() });
  const queue = useQuery({ queryKey: ["planner", "assignments", queueTab], queryFn: () => api.plannerAssignments(queueTab) });
  const audits = useQuery({ queryKey: ["planner", "audits"], queryFn: () => api.plannerAudits() });
  const activity = useQuery({ queryKey: ["planner", "activity"], queryFn: () => api.plannerActivity(50) });

  const refetch = () => { qc.invalidateQueries({ queryKey: ["planner"] }); setSelected(new Set()); };
  const undo = useMutation({ mutationFn: (id: string) => api.plannerUndo(id), onSuccess: refetch });
  const suggest = useMutation({ mutationFn: () => api.plannerSuggest(useLlm), onSuccess: refetch });
  const bulk = useMutation({ mutationFn: (v: { ids: string[]; action: "accept" | "reject" | "reopen"; kind?: "assignment" | "keyword" }) => api.plannerBulk(v.ids, v.action, v.kind ?? "assignment"), onSuccess: refetch });
  const reassign = useMutation({ mutationFn: (v: { id: string; clusterId: string; kind?: "assignment" | "keyword" }) => api.plannerReassign(v.id, v.clusterId, v.kind ?? "assignment"), onSuccess: refetch });
  const setPrimary = useMutation({ mutationFn: (v: { id: string; kind?: "assignment" | "keyword" }) => api.plannerSetPrimary(v.id, v.kind ?? "assignment"), onSuccess: refetch });
  // Rule 1: when a LIVE page already targets this cluster's primary keyword the
  // server refuses and explains which page. The operator can still override —
  // the guard is advice here, and a hard stop for anything unattended.
  const createPiece = useMutation({
    mutationFn: (clusterId: string) => api.plannerCreatePiece(clusterId),
    onSuccess: (r, clusterId) => {
      if (r.mode === "rival-blocked") {
        const ok = window.confirm(
          `${r.rival?.reason ?? "A live page already targets this keyword."}\n\n` +
          `Nothing was created. Build it anyway only if this is genuinely a DIFFERENT page ` +
          `(e.g. studying a method vs receiving it).\n\nBuild anyway?`,
        );
        if (ok) createPieceForced.mutate(clusterId);
        return;
      }
      refetch();
    },
  });
  const createPieceForced = useMutation({ mutationFn: (clusterId: string) => api.plannerCreatePiece(clusterId, true), onSuccess: refetch });
  const expand = useMutation({ mutationFn: (clusterId: string) => api.plannerExpand(clusterId, useLlm), onSuccess: () => { qc.invalidateQueries({ queryKey: ["planner"] }); setQueueTab("pending"); } });
  const reconcile = useMutation({ mutationFn: () => api.reconcileSections(), onSuccess: refetch });
  const busy = suggest.isPending || bulk.isPending || reassign.isPending || setPrimary.isPending || createPiece.isPending || createPieceForced.isPending || expand.isPending || reconcile.isPending;

  const clusters = tree.data?.clusters ?? [];
  const families = tree.data?.families ?? [];

  // family → subFamily (or "") → clusters, filtered by the search box.
  const grouped = useMemo(() => {
    const q = clusterFilter.trim().toLowerCase();
    const map = new Map<string, Map<string, PlannerClusterNode[]>>();
    for (const c of clusters) {
      if (q && !c.nameHe.toLowerCase().includes(q) && !c.nameEn.toLowerCase().includes(q) && !c.id.includes(q)) continue;
      if (actionFilter && c.action !== actionFilter) continue;
      const fam = map.get(c.family) ?? new Map<string, PlannerClusterNode[]>();
      const sub = fam.get(c.subFamily ?? "") ?? [];
      sub.push(c);
      fam.set(c.subFamily ?? "", sub);
      map.set(c.family, fam);
    }
    return map;
  }, [clusters, clusterFilter, actionFilter]);

  // Priorities view — clusters ranked by SEO opportunity (server-computed).
  // "Build only" keeps just the create-page work (no live page yet); off shows
  // everything (incl. expand-existing) so live pages worth strengthening surface.
  const prioritized = useMemo(() => {
    const q = clusterFilter.trim().toLowerCase();
    return clusters
      .filter((c) => c.opportunity > 0)
      .filter((c) => (prioBuildOnly ? c.action === "create-page" : true))
      .filter((c) => !q || c.nameHe.toLowerCase().includes(q) || c.nameEn.toLowerCase().includes(q) || c.id.includes(q))
      .sort((a, b) => b.opportunity - a.opportunity);
  }, [clusters, prioBuildOnly, clusterFilter]);

  const items = (queue.data?.items ?? []).filter((i) => !selectedCluster || i.clusterId === selectedCluster);
  const selNode = selectedCluster ? clusters.find((c) => c.id === selectedCluster) ?? null : null;
  const tabCount = (t: "pending" | "accepted" | "rejected") =>
    selNode ? (t === "pending" ? selNode.pendingCount : t === "accepted" ? selNode.keywordCount : selNode.rejectedCount)
            : (queue.data?.counts[t] ?? 0);

  // Selecting a cluster lands you on the tab that actually has its keywords, so
  // it never looks empty because you were on the wrong tab.
  const selectCluster = (c: PlannerClusterNode) => {
    if (selectedCluster === c.id) { setSelectedCluster(null); return; }
    setSelectedCluster(c.id);
    setSelected(new Set());
    const cur = queueTab === "pending" ? c.pendingCount : queueTab === "accepted" ? c.keywordCount : c.rejectedCount;
    if (cur === 0) {
      if (c.pendingCount > 0) setQueueTab("pending");
      else if (c.keywordCount > 0) setQueueTab("accepted");
      else if (c.rejectedCount > 0) setQueueTab("rejected");
    }
  };
  const toggleFamily = (id: string) => setOpenFamilies((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelect = (id: string) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));

  return (
    <div className="space-y-3">
      {/* ── How-it-works explainer (dismissible) ── */}
      {showHelp && (
        <div className="card border-brand-200 bg-brand-50/40 p-3 text-xs text-ink">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-1.5">
              <p className="font-semibold">How the Planner works — one cluster = one page</p>
              <ol className="ml-4 list-decimal space-y-0.5 text-muted">
                <li><strong className="text-ink">The tree (left)</strong> is your full cluster architecture: {`family → sub-family → cluster`}. Each cluster will own exactly ONE page. They start empty — the number on each row counts <strong className="text-ink">accepted keywords</strong>, so everything shows 0 until you review the queue.</li>
                <li><strong className="text-ink">Import keywords</strong> (CSV/Excel or single-add above) → they land in the research store.</li>
                <li><strong className="text-ink">Suggest assignments</strong> matches every keyword to its cluster — free deterministic rules first (מחיר → pricing row, מול/או → comparison, city → location, condition beats method → bridge), then an optional LLM pass for the rest.</li>
                <li><strong className="text-ink">Review the queue (right)</strong> — accept ✓, reject ✗, or reassign ⚖. Accepting a <span className="rounded bg-emerald-50 px-1 text-emerald-800">NEW</span> proposal also creates that cluster. Accepted keywords fill the tree and drive each cluster's 3–15 size gauge.</li>
                <li><strong className="text-ink">Then act:</strong> each cluster shows its next action (Review / Create page / <strong className="text-ink">Fold</strong> — a section like "price" folds into its parent page instead of getting its own / Live); <strong className="text-ink">Expand (DFS)</strong> grows a cluster and routes results to the right clusters; <strong className="text-ink">Create content piece</strong> turns a cluster into one article (primary + secondaries); <strong className="text-ink">Audits</strong> flag cannibalization, doorway risk & supply gaps. Switch to the <strong className="text-ink">Map</strong> for the whole architecture at a glance.</li>
              </ol>
            </div>
            <button onClick={dismissHelp} className="btn-icon shrink-0" title="Dismiss (reopen via the ? button)"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {/* ── Action bar ── */}
      <div className="card flex flex-wrap items-center gap-2 p-3">
        <button className="btn-primary btn-sm" disabled={busy} onClick={() => suggest.mutate()}
          title="Match every unassigned/pending research keyword to a cluster. Free deterministic rules first; the LLM pass covers the rest. Never touches keywords you already accepted or rejected.">
          {suggest.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Suggest assignments
        </button>
        <label className="flex items-center gap-1 text-xs text-muted" title="Deterministic matching is free; the LLM pass classifies whatever it can't resolve (~fractions of a cent per 60 keywords).">
          <input type="checkbox" checked={useLlm} onChange={(e) => setUseLlm(e.target.checked)} /> LLM pass
        </label>
        {!showHelp && (
          <button onClick={() => { localStorage.removeItem("planner-help-dismissed"); setShowHelp(true); }} className="btn-icon" title="How does the Planner work?">
            <HelpCircle className="h-4 w-4" />
          </button>
        )}
        {audits.data && (() => {
          const { error, warning } = audits.data.counts;
          const clean = error === 0 && warning === 0;
          return (
            <button onClick={() => setShowAudits((s) => !s)}
              title="Architecture audits: cannibalization, PRO isolation, oversized clusters, doorway-page risk, supply gates."
              className={`btn-sm ${showAudits ? "btn-primary" : "btn-secondary"}`}>
              {clean ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> : <ShieldAlert className="h-3.5 w-3.5 text-amber-600" />}
              Audits {clean ? "✓" : `· ${error ? `${error}⛔ ` : ""}${warning}⚠`}
            </button>
          );
        })()}
        <button onClick={() => setShowActivity((s) => !s)}
          title="Recent actions you took here (accept / reject / reassign / set primary) — each can be undone."
          className={`btn-sm ${showActivity ? "btn-primary" : "btn-secondary"}`}>
          <History className="h-3.5 w-3.5" />
          Activity{activity.data && activity.data.entries.length > 0 ? ` · ${activity.data.entries.filter((e) => !e.undone).length}` : ""}
        </button>
        <button className="btn-secondary btn-sm" disabled={busy} onClick={() => setShowNewCluster(true)} title="Add a new subcluster — placed in the taxonomy (family / parent / page type).">
          <FolderPlus className="h-3.5 w-3.5" /> New cluster
        </button>
        {(tree.data?.actionCounts.fold ?? 0) > 0 && (
          <button className="btn-secondary btn-sm" disabled={busy} onClick={() => { if (window.confirm("Move all 'price' sections off the commercial hub onto the editorial /categories page? Reversible per-cluster via Edit → Move.")) reconcile.mutate(); }}
            title="One-time: re-parent section clusters (price) from the /explore hub onto the method's editorial /categories page, so they fold there.">
            {reconcile.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Fix section parents
          </button>
        )}
        {tree.data && (
          <span className="ml-auto flex items-center gap-3 text-xs text-muted">
            <span title="Clusters in the taxonomy — each will own exactly one page.">{clusters.length} clusters</span>
            <span className="text-amber-700" title="Keyword→cluster proposals waiting for your review in the queue.">{tree.data.counts.pending} pending</span>
            <span className="text-emerald-700" title="Keywords you accepted — these count into the tree and the size gauges.">{tree.data.counts.accepted} assigned</span>
          </span>
        )}
        {suggest.data && <p className="w-full text-xs text-emerald-700">{suggest.data.summary}</p>}
        {suggest.data && suggest.data.warnings.length > 0 && <p className="w-full text-2xs text-amber-700">{suggest.data.warnings.join(" · ")}</p>}
        {suggest.isError && <p className="w-full text-xs text-danger">{(suggest.error as Error).message}</p>}
        {reconcile.data && <p className="w-full text-xs text-emerald-700">{reconcile.data.summary}</p>}
        {createPiece.data && <p className="w-full text-xs text-emerald-700" dir="auto">{createPiece.data.summary}</p>}
        {createPiece.isError && <p className="w-full text-xs text-danger">{(createPiece.error as Error).message}</p>}
      </div>

      {/* ── Audits panel ── */}
      {showAudits && audits.data && (
        <div className="card p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
            Architecture audits
            <span className="text-2xs font-normal text-muted">— the plan's rules checked against your taxonomy + live site ({audits.data.checkedClusters} clusters)</span>
            <button onClick={() => setShowAudits(false)} className="btn-icon ml-auto"><X className="h-4 w-4" /></button>
          </div>
          {audits.data.findings.length === 0 ? (
            <p className="flex items-center gap-1.5 text-sm text-emerald-700"><ShieldCheck className="h-4 w-4" /> No issues — the architecture is sound (one page per cluster, no cannibalization, PRO isolated).</p>
          ) : (
            <div className="space-y-1.5">
              {audits.data.findings.map((f, i) => (
                <AuditRow key={i} f={f} onSelect={(id) => { const c = clusters.find((x) => x.id === id); if (c) selectCluster(c); }} />
              ))}
            </div>
          )}
          <p className="mt-2 text-2xs text-muted">Live link rules (every spoke → its pillar, every editorial page → a money page) are checked in the <strong>Link map</strong> tab as pages go live.</p>
        </div>
      )}

      {/* ── Activity feed + Undo ── */}
      {showActivity && (
        <div className="card p-3">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
            <History className="h-4 w-4 text-brand-700" /> Recent activity
            <span className="text-2xs font-normal text-muted">— what you changed here; Undo reverts an action exactly</span>
            <button onClick={() => setShowActivity(false)} className="btn-icon ml-auto"><X className="h-4 w-4" /></button>
          </div>
          {!activity.data || activity.data.entries.length === 0 ? (
            <p className="text-sm text-muted">No actions yet. Accept, reject, reassign or set-primary here and they'll appear — each undoable.</p>
          ) : (
            <div className="max-h-72 space-y-1 overflow-y-auto scrollbar-thin">
              {activity.data.entries.map((e) => (
                <div key={e.id} className={`flex items-center gap-2 rounded-md border border-line px-2.5 py-1.5 text-xs ${e.undone ? "opacity-50" : ""}`}>
                  <span className="shrink-0 font-mono text-3xs text-muted" title={e.occurredAt}>
                    {new Date(e.occurredAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-ink" dir="auto" title={e.items.map((it) => it.keyword).join(", ")}>
                    {e.summary}
                    {e.itemCount === 1 && e.items[0] ? <span className="text-muted"> — {e.items[0].keyword}</span> : null}
                  </span>
                  {e.undone ? (
                    <span className="shrink-0 text-3xs text-muted">undone</span>
                  ) : (
                    <button
                      onClick={() => undo.mutate(e.id)}
                      disabled={undo.isPending}
                      className="btn-icon shrink-0 text-muted hover:text-brand-700"
                      title="Undo this action — restores each keyword to its previous state"
                    >
                      {undo.isPending && undo.variables === e.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Map and Priorities both want the full width — a ranked list of every
          cluster reads badly in a 340px column next to the assignment queue. */}
      <div className={viewMode === "map" || viewMode === "priorities" ? "space-y-3" : "grid gap-3 lg:grid-cols-[minmax(340px,2fr)_3fr]"}>
        {/* ── Taxonomy tree / map ── */}
        <div className={`card p-3 ${viewMode === "map" ? "" : "max-h-[70vh] overflow-y-auto scrollbar-thin"}`}>
          <div className="mb-2 flex items-center gap-2">
            <span className="flex overflow-hidden rounded-lg border border-line">
              <button onClick={() => setViewMode("tree")} title="Tree view"
                className={`flex items-center gap-1 px-2 py-1 text-xs ${viewMode === "tree" ? "bg-brand-50 font-medium text-brand-800" : "text-muted hover:bg-line/40"}`}>
                <ListTree className="h-3.5 w-3.5" /> Tree
              </button>
              <button onClick={() => setViewMode("map")} title="Map view — the whole architecture at a glance"
                className={`flex items-center gap-1 px-2 py-1 text-xs ${viewMode === "map" ? "bg-brand-50 font-medium text-brand-800" : "text-muted hover:bg-line/40"}`}>
                <MapIcon className="h-3.5 w-3.5" /> Map
              </button>
              <button onClick={() => setViewMode("priorities")} title="Priorities — clusters ranked by SEO opportunity (volume × winnability × intent)"
                className={`flex items-center gap-1 px-2 py-1 text-xs ${viewMode === "priorities" ? "bg-brand-50 font-medium text-brand-800" : "text-muted hover:bg-line/40"}`}>
                <TrendingUp className="h-3.5 w-3.5" /> Priorities
              </button>
            </span>
            {viewMode !== "map" && (
              <>
                <SearchIcon className="h-3.5 w-3.5 text-muted" />
                <input className="input py-1 text-xs" placeholder="Filter clusters…" value={clusterFilter} onChange={(e) => setClusterFilter(e.target.value)} />
              </>
            )}
          </div>
          {viewMode === "map" && tree.data && (
            <PlannerMap families={families} clusters={clusters} selectedId={selectedCluster} onSelect={selectCluster} />
          )}
          {viewMode === "map" && tree.isPending && <div className="text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading taxonomy…</div>}
          {/* Work-queue summary — click a chip to filter the tree to that action. */}
          {viewMode === "tree" && tree.data && (
            <div className="mb-2 flex flex-wrap items-center gap-1">
              {WORK_ACTIONS.map((k) => {
                const n = tree.data!.actionCounts[k] ?? 0;
                if (n === 0) return null;
                const active = actionFilter === k;
                return (
                  <button key={k} onClick={() => setActionFilter(active ? null : k)}
                    title={ACTION_STYLE[k].hint}
                    className={`rounded px-1.5 py-0.5 text-2xs font-medium ${ACTION_STYLE[k].cls} ${active ? "ring-2 ring-offset-1 ring-brand-400" : "opacity-90 hover:opacity-100"}`}>
                    {n} {k === "create-page" ? "to create" : k === "expand-page" ? "to expand" : k === "fold" ? "to fold" : k}
                  </button>
                );
              })}
              {actionFilter && <button onClick={() => setActionFilter(null)} className="text-2xs text-muted hover:text-danger">clear</button>}
            </div>
          )}
          {viewMode === "tree" && tree.isPending && <div className="text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading taxonomy…</div>}
          {viewMode === "tree" && families.map((f) => {
            const fam = grouped.get(f.id);
            if (!fam && clusterFilter) return null;
            const famClusters = fam ? [...fam.values()].flat() : [];
            const open = openFamilies.has(f.id) || !!clusterFilter;
            const totalKw = famClusters.reduce((s, c) => s + c.keywordCount, 0);
            const totalPending = famClusters.reduce((s, c) => s + c.pendingCount, 0);
            return (
              <div key={f.id} className="mb-1">
                <button onClick={() => toggleFamily(f.id)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-line/40"
                  title={`${f.nameEn} — ${f.description}${f.money ? " (money pages: booking/contact intent)" : " (editorial: captures demand, routes to money pages)"}`}>
                  {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted" />}
                  {f.money ? <CircleDollarSign className="h-3.5 w-3.5 shrink-0 text-emerald-600" /> : <FileText className="h-3.5 w-3.5 shrink-0 text-blue-500" />}
                  <span className="text-sm font-semibold text-ink">{f.id}</span>
                  <span className="truncate text-xs text-muted">{f.nameHe}</span>
                  <span className="ml-auto shrink-0 text-2xs text-muted" title={`${famClusters.length} clusters · ${totalKw} accepted keyword(s)${totalPending ? ` · ${totalPending} pending review` : ""}`}>{famClusters.length} · {totalKw} kw{totalPending ? ` · ${totalPending}⏳` : ""}</span>
                </button>
                {open && fam && [...fam.entries()].map(([sub, list]) => (
                  <div key={sub || "_root"} className="ml-5 border-l border-line pl-2">
                    {sub && <div className="mt-1 px-1 text-2xs font-medium uppercase tracking-wide text-muted">{sub}</div>}
                    {list.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => selectCluster(c)}
                        className={`flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-xs hover:bg-line/40 ${selectedCluster === c.id ? "bg-brand-50 ring-1 ring-brand-200" : ""}`}
                      >
                        <IntentChip intent={c.intentLabel} />
                        <span dir="rtl" className="truncate text-ink" title={`${c.id} — ${c.nameEn}${c.description ? `\n${c.description}` : ""}`}>{c.nameHe}</span>
                        {c.pageType && <span className="shrink-0 rounded bg-line/60 px-1 text-[10px] text-muted" title={`Page type: ${PAGE_TYPE_LABEL[c.pageType]} — the ONE page this cluster will own`}>{PAGE_TYPE_LABEL[c.pageType]}</span>}
                        {c.parentClusterId && <span className="shrink-0 text-[10px] text-muted" title={`Spoke of ${c.parentClusterId} — must link up to its pillar`}>↳</span>}
                        {c.supplyGate != null && <span title={`Supply gate: don't build this page before ${c.supplyGate} live therapists (doorway-page rule)`}><Users className="h-3 w-3 shrink-0 text-amber-500" /></span>}
                        <span className="ml-auto flex shrink-0 items-center gap-1.5">
                          {c.needsVolumes && <span className="text-[10px] text-amber-600" title="Some accepted keywords have no search volume — fetch volumes (All keywords tab)">⚠vol</span>}
                          {c.totalVolume > 0 && <span className="text-[10px] text-muted">{c.totalVolume.toLocaleString()}</span>}
                          <SizeGauge n={c.keywordCount} />
                          {/* Action chip — clickable for create/expand (span, not a nested button). */}
                          {c.actionLabel && (
                            (c.action === "create-page" || c.action === "expand-page" || c.action === "fold") ? (
                              <span role="button" tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); if (!busy) createPiece.mutate(c.id); }}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); createPiece.mutate(c.id); } }}
                                title={ACTION_STYLE[c.action].hint}
                                className={`cursor-pointer rounded px-1 text-[10px] font-medium ${ACTION_STYLE[c.action].cls} hover:brightness-95`}>
                                {c.actionLabel}
                              </span>
                            ) : (
                              <span title={ACTION_STYLE[c.action].hint} className={`rounded px-1 text-[10px] font-medium ${ACTION_STYLE[c.action].cls}`}>{c.actionLabel}</span>
                            )
                          )}
                          {c.phase && <span className="rounded bg-line/60 px-1 text-[10px] text-muted" title={`Build phase ${c.phase}`}>P{c.phase}</span>}
                          <span role="button" tabIndex={0} title="Edit / move / archive this cluster"
                            onClick={(e) => { e.stopPropagation(); setEditCluster(c); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setEditCluster(c); } }}
                            className="cursor-pointer rounded p-0.5 text-muted hover:bg-line hover:text-ink">
                            <Pencil className="h-3 w-3" />
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}

          {/* ── Priorities: clusters ranked by SEO opportunity ── */}
          {viewMode === "priorities" && tree.isPending && <div className="text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div>}
          {viewMode === "priorities" && tree.data && (
            <>
              <div className="mb-2 flex items-center gap-2 text-2xs text-muted">
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={prioBuildOnly} onChange={(e) => setPrioBuildOnly(e.target.checked)} />
                  Build-ready only
                </label>
                <span className="ml-auto" title="Opportunity = Σ (volume × winnability × intent weight) over the cluster's kept keywords. Winnability = 1 − KD/100 (unknown KD → 25); navigational is discounted. Reach lean.">
                  {prioritized.length} ranked · by opportunity ⓘ
                </span>
              </div>
              <div className="space-y-0.5">
                {prioritized.map((c, i) => (
                  <button key={c.id} onClick={() => selectCluster(c)}
                    className={`flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-xs hover:bg-line/40 ${selectedCluster === c.id ? "bg-brand-50 ring-1 ring-brand-200" : ""}`}>
                    <span className="w-5 shrink-0 text-right font-mono text-[10px] text-muted">{i + 1}</span>
                    <span className="w-14 shrink-0 text-right font-mono text-xs font-semibold text-brand-800" title="Opportunity score">{c.opportunity.toLocaleString()}</span>
                    <IntentChip intent={c.intentLabel} />
                    <span dir="rtl" className="min-w-0 truncate text-ink" title={`${c.id} — ${c.nameEn}`}>{c.nameHe}</span>
                    <span className="ml-auto flex shrink-0 items-center gap-1.5">
                      {c.supplyGate != null && <span title={`Supply gate: don't build before ${c.supplyGate} live therapists`}><Users className="h-3 w-3 text-amber-500" /></span>}
                      {c.needsVolumes && <span className="text-[10px] text-amber-600" title="Some keywords lack a volume — the score understates this cluster">⚠vol</span>}
                      <span className="text-[10px] text-muted" title="Total monthly search volume">{c.totalVolume.toLocaleString()}/mo</span>
                      <SizeGauge n={c.keywordCount} />
                      {c.actionLabel && (
                        (c.action === "create-page" || c.action === "expand-page" || c.action === "fold") ? (
                          <span role="button" tabIndex={0}
                            onClick={(e) => { e.stopPropagation(); if (!busy) createPiece.mutate(c.id); }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); createPiece.mutate(c.id); } }}
                            title={ACTION_STYLE[c.action].hint}
                            className={`cursor-pointer rounded px-1 text-[10px] font-medium ${ACTION_STYLE[c.action].cls} hover:brightness-95`}>
                            {c.actionLabel}
                          </span>
                        ) : (
                          <span title={ACTION_STYLE[c.action].hint} className={`rounded px-1 text-[10px] font-medium ${ACTION_STYLE[c.action].cls}`}>{c.actionLabel}</span>
                        )
                      )}
                    </span>
                  </button>
                ))}
                {prioritized.length === 0 && <div className="py-8 text-center text-sm text-muted">No scored clusters{prioBuildOnly ? " to build" : ""}. {prioBuildOnly ? "Uncheck 'Build-ready only' to see live pages too." : ""}</div>}
              </div>
            </>
          )}
        </div>

        {/* ── Assignment queue ── */}
        <div className="card max-h-[70vh] overflow-y-auto p-3 scrollbar-thin">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {(["pending", "accepted", "rejected"] as const).map((t) => (
              <button key={t} onClick={() => { setQueueTab(t); setSelected(new Set()); }}
                title={(selNode ? `${selNode.nameHe}: ` : "") + (t === "pending" ? "Engine proposals awaiting your review — nothing is assigned until you accept" : t === "accepted" ? "Keywords you approved — they count into the tree" : "Rejected proposals — reopen to reconsider")}
                className={`rounded-lg px-2.5 py-1 text-xs ${queueTab === t ? "bg-brand-50 font-medium text-brand-800" : "text-muted hover:bg-line/50"}`}>
                {t} ({tabCount(t)})
              </button>
            ))}
            {selectedCluster && (
              <span className="flex items-center gap-1 rounded bg-brand-50 px-2 py-0.5 text-2xs text-brand-800">
                <Filter className="h-3 w-3" /> {clusters.find((c) => c.id === selectedCluster)?.nameHe ?? selectedCluster}
                <button onClick={() => setSelectedCluster(null)} className="hover:text-danger"><X className="h-3 w-3" /></button>
              </span>
            )}
            {selectedCluster && (() => {
              const sc = clusters.find((c) => c.id === selectedCluster);
              if (!sc) return null;
              return (
                <span className="flex items-center gap-1.5">
                  <button onClick={() => expand.mutate(selectedCluster)} disabled={busy} className="btn-secondary btn-sm"
                    title={`Fetch related keywords from DataForSEO seeded on "${sc.nameHe}" → each is routed to its right cluster by the engine (ownership rules apply). Costs a few cents.`}>
                    {expand.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sprout className="h-3.5 w-3.5" />} Expand (DFS)
                  </button>
                  {sc.pageUrl && (
                    <a href={sc.pageUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-2xs text-emerald-800" title="This cluster already owns a live page — expand it rather than create a competing piece (rule 1).">
                      <ExternalLink className="h-3 w-3" /> live page
                    </a>
                  )}
                  {sc.keywordCount > 0 && (
                    <button onClick={() => createPiece.mutate(selectedCluster)} disabled={busy} className="btn-secondary btn-sm"
                      title="Promote the cluster's PRIMARY keyword (★, else highest volume) into the Pipeline, carrying the other accepted keywords as secondaries → the brief writes them as sections/FAQ.">
                      <FilePlus2 className="h-3.5 w-3.5" /> Create content piece
                    </button>
                  )}
                  {sc.keywordCount === 0 && sc.pendingCount > 0 && (
                    <span className="text-2xs text-muted">Accept its {sc.pendingCount} pending keyword(s) below → then “Create content piece”.</span>
                  )}
                </span>
              );
            })()}
            {selected.size > 0 && queueTab === "pending" && (
              <span className="ml-auto flex items-center gap-1.5">
                <span className="text-2xs text-muted">{selected.size} selected</span>
                <button className="btn-primary btn-sm" disabled={busy} onClick={() => bulk.mutate({ ids: [...selected], action: "accept" })}><Check className="h-3.5 w-3.5" /> Accept</button>
                <button className="btn-secondary btn-sm" disabled={busy} onClick={() => bulk.mutate({ ids: [...selected], action: "reject" })}>Reject</button>
              </span>
            )}
          </div>
          {createPiece.data && <p className="mb-1.5 rounded bg-emerald-50 px-2 py-1 text-2xs text-emerald-800">{createPiece.data.summary}{createPiece.data.mode === "created" && " Find it in the Pipeline (status: backlog)."}</p>}
          {createPiece.isError && <p className="mb-1.5 text-2xs text-danger">{(createPiece.error as Error).message}</p>}
          {expand.data && <p className="mb-1.5 rounded bg-brand-50 px-2 py-1 text-2xs text-brand-800">{expand.data.summary}</p>}
          {expand.isError && <p className="mb-1.5 text-2xs text-danger">{(expand.error as Error).message}</p>}
          {items.length > 0 && queueTab === "pending" && (
            <label className="mb-1 flex items-center gap-1.5 px-1 text-2xs text-muted">
              <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)))} /> Select all ({items.length})
            </label>
          )}
          {queue.isPending && <div className="text-sm text-muted"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div>}
          {queue.data && items.length === 0 && (
            <div className="py-8 text-center text-sm text-muted">
              {selNode ? (
                <>
                  No {queueTab} keywords in <span className="font-medium text-ink" dir="rtl">{selNode.nameHe}</span>.
                  {selNode.pendingCount > 0 && queueTab !== "pending" && <> It has <button className="text-brand-700 underline" onClick={() => setQueueTab("pending")}>{selNode.pendingCount} pending</button> to review.</>}
                  {selNode.keywordCount > 0 && queueTab !== "accepted" && <> {selNode.keywordCount} accepted.</>}
                  {selNode.pendingCount === 0 && selNode.keywordCount === 0 && <> It's empty — use <strong>Expand (DFS)</strong> above to fetch keywords.</>}
                </>
              ) : queueTab === "pending" ? "No pending assignments. Import keywords (CSV/Excel above), then run Suggest assignments." : `No ${queueTab} assignments.`}
            </div>
          )}
          <div className="space-y-1.5">
            {items.map((i) => (
              <AssignmentCard key={i.id} a={i} clusters={clusters} busy={busy}
                selected={selected.has(i.id)} onToggle={() => toggleSelect(i.id)}
                onAccept={() => bulk.mutate({ ids: [i.id], action: "accept", kind: i.kind })}
                onReject={() => bulk.mutate({ ids: [i.id], action: "reject", kind: i.kind })}
                onReopen={() => bulk.mutate({ ids: [i.id], action: "reopen", kind: i.kind })}
                onReassign={(clusterId) => reassign.mutate({ id: i.id, clusterId, kind: i.kind })}
                onSetPrimary={() => setPrimary.mutate({ id: i.id, kind: i.kind })}
              />
            ))}
          </div>
        </div>
      </div>

      {editCluster && (
        <ClusterEditModal
          cluster={editCluster}
          allClusters={clusters}
          onClose={() => setEditCluster(null)}
          onSaved={() => { /* tree invalidated inside the modal */ }}
        />
      )}
      {showNewCluster && (
        <NewClusterDialog
          allClusters={clusters}
          onCancel={() => setShowNewCluster(false)}
          onCreated={() => { setShowNewCluster(false); refetch(); }}
        />
      )}
    </div>
  );
}

function AuditRow({ f, onSelect }: { f: PlannerAuditFinding; onSelect: (id: string) => void }) {
  const tone = f.severity === "error" ? "border-danger/40 bg-danger/5" : f.severity === "warning" ? "border-amber-300 bg-amber-50" : "border-line bg-canvas";
  const Icon = f.severity === "error" ? ShieldAlert : f.severity === "warning" ? ShieldAlert : Info;
  const iconCls = f.severity === "error" ? "text-danger" : f.severity === "warning" ? "text-amber-600" : "text-muted";
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-2.5 py-1.5 text-xs ${tone}`}>
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${iconCls}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="rounded bg-white/70 px-1 text-[10px] font-medium text-muted">{f.kind}</span>
          {f.clusterId ? (
            <button onClick={() => onSelect(f.clusterId)} dir="rtl" className="font-medium text-brand-700 hover:underline">{f.clusterNameHe}</button>
          ) : <span dir="rtl" className="font-medium text-ink">{f.clusterNameHe}</span>}
        </div>
        <p className="mt-0.5 text-muted" dir="auto">{f.message}</p>
      </div>
    </div>
  );
}

function AssignmentCard({ a, clusters, busy, selected, onToggle, onAccept, onReject, onReopen, onReassign, onSetPrimary }: {
  a: PlannerAssignmentView; clusters: PlannerClusterNode[]; busy: boolean;
  selected: boolean; onToggle: () => void;
  onAccept: () => void; onReject: () => void; onReopen: () => void; onReassign: (clusterId: string) => void;
  onSetPrimary: () => void;
}) {
  const [showReassign, setShowReassign] = useState(false);
  const target = a.clusterId
    ? { kind: "existing" as const, label: a.clusterNameHe ?? a.clusterId }
    : a.proposedNewCluster
      ? { kind: "new" as const, label: a.proposedNewCluster.nameHe, sub: `${a.proposedNewCluster.family} · ${PAGE_TYPE_LABEL[a.proposedNewCluster.pageType]} · ${a.proposedNewCluster.idSuggestion}` }
      : { kind: "none" as const, label: "needs manual assignment" };

  return (
    <div className="rounded-lg border border-line px-2.5 py-2">
      <div className="flex items-center gap-2">
        {a.status === "pending" && <input type="checkbox" checked={selected} onChange={onToggle} />}
        {a.status === "accepted" && a.isPrimary && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" aria-label="Primary keyword — becomes the article's main keyword" />}
        <span dir="rtl" className="min-w-0 truncate text-sm font-medium text-ink">{a.keyword}</span>
        {a.searchVolume != null && <span className="shrink-0 text-2xs text-muted">{a.searchVolume.toLocaleString()}/mo</span>}
        <span className="shrink-0 text-muted">→</span>
        {target.kind === "existing" && <span dir="rtl" className="truncate rounded bg-brand-50 px-1.5 py-0.5 text-xs text-brand-800">{target.label}</span>}
        {target.kind === "new" && (
          <span className="flex min-w-0 items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-800" title={target.sub}>
            <Landmark className="h-3 w-3 shrink-0" /> <span dir="rtl" className="truncate">NEW: {target.label}</span>
          </span>
        )}
        {target.kind === "none" && <span className="flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700"><AlertTriangle className="h-3 w-3" /> {target.label}</span>}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          {a.status === "pending" && (
            <>
              <button onClick={onAccept} disabled={busy} className="btn-primary btn-sm" title={target.kind === "new" ? "Accept → creates the cluster + assigns" : "Accept assignment"}><Check className="h-3.5 w-3.5" /></button>
              <button onClick={() => setShowReassign((s) => !s)} disabled={busy} className="btn-icon" title="Reassign to a different cluster"><Scale className="h-4 w-4" /></button>
              <button onClick={onReject} disabled={busy} className="btn-icon" title="Reject"><X className="h-4 w-4" /></button>
            </>
          )}
          {a.status === "accepted" && (
            <>
              {!a.isPrimary && a.clusterId && (
                <button onClick={onSetPrimary} disabled={busy} className="btn-icon" title="Make this the cluster's PRIMARY keyword (the article's main keyword; others become secondaries)"><Star className="h-4 w-4" /></button>
              )}
              <button onClick={() => setShowReassign((s) => !s)} disabled={busy} className="btn-icon" title="Move to a different cluster"><Scale className="h-4 w-4" /></button>
              <button onClick={onReject} disabled={busy} className="btn-icon" title="Reject (remove from this cluster)"><X className="h-4 w-4" /></button>
            </>
          )}
          {a.status === "rejected" && <button onClick={onReopen} disabled={busy} className="btn-ghost btn-sm text-xs">Reopen</button>}
        </span>
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 pl-6 text-2xs text-muted">
        <span className="rounded bg-line/60 px-1">{a.source}</span>
        {a.matchedSignal && a.matchedSignal !== "unresolved" && <span className="rounded bg-line/60 px-1">{a.matchedSignal}</span>}
        {a.confidence != null && <span>{Math.round(a.confidence * 100)}%</span>}
        {a.rationale && <span className="truncate" title={a.rationale}>{a.rationale}</span>}
      </div>
      {showReassign && (
        <div className="mt-1.5 pl-6">
          <select className="input py-1 text-xs" defaultValue="" onChange={(e) => { if (e.target.value) { onReassign(e.target.value); setShowReassign(false); } }}>
            <option value="">Reassign to…</option>
            {clusters.map((c) => <option key={c.id} value={c.id}>{c.family}{c.subFamily ? `/${c.subFamily}` : ""} — {c.nameHe}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}
